/**
 * Ein Prüfschritt — die Arbeitsfläche, die „Prüfung starten" öffnet.
 *
 * Oben die Nummer, der Titel und der eine Satz aus der Checkliste, was zu
 * tun ist. Dann die Unterlagen dieses Schritts und das Werkzeug, das zu
 * ihm gehört: der Saldenabgleich in Schritt 1, der Abschluss in Schritt 5
 * (Schritt 3 öffnet gleich die Belegansicht, nicht diese Seite). Zum
 * Schluss der Knopf, der den Schritt erledigt. Bewusst ohne Merkliste —
 * eine Seite, die man auf einen Blick erfasst.
 */

import { useEffect, useState, type ReactNode } from 'react';

import type { Dokument, Kategorie, Rolle, Schritt } from '../../lib/typen';
import { UnterlagenListe } from './Unterlagen';

export interface SchrittSeiteProps {
  pruefungId?: string;
  schritt: Schritt;
  schritte: Schritt[];
  kategorien: Kategorie[];
  dokumente: Dokument[];
  rolle: Rolle;
  abgeschlossen: boolean;
  offeneRueckfragen: number;
  onDokument: (dokumentId: string) => void;
  onStatus: (nummer: number, status: 'offen' | 'laufend' | 'erledigt', anmerkung?: string) => Promise<void>;
  onRueckfrage: (nummer: number) => void;
  onWechsel: (nummer: number) => void;
  onZurueck: () => void;
  /** Das Werkzeug des Schritts. */
  children?: ReactNode;
}

export function SchrittSeite({
  schritt,
  schritte,
  kategorien,
  dokumente,
  rolle,
  abgeschlossen,
  offeneRueckfragen,
  onDokument,
  onStatus,
  onRueckfrage,
  onWechsel,
  onZurueck,
  children,
}: SchrittSeiteProps) {
  const [anmerkung, setAnmerkung] = useState(schritt.anmerkung ?? '');
  const [laeuft, setLaeuft] = useState(false);
  const istBeirat = rolle === 'beirat';
  const erledigt = schritt.status === 'erledigt';

  // Beim Öffnen gilt ein offener Schritt als begonnen — das ist der Stand,
  // den die Übersicht als „Fortsetzen" zeigt.
  useEffect(() => {
    if (istBeirat && schritt.status === 'offen' && !abgeschlossen) void onStatus(schritt.nummer, 'laufend');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schritt.nummer]);

  useEffect(() => {
    setAnmerkung(schritt.anmerkung ?? '');
  }, [schritt.nummer, schritt.anmerkung]);

  async function abschliessen() {
    setLaeuft(true);
    try {
      await onStatus(schritt.nummer, 'erledigt', anmerkung.trim() || undefined);
    } finally {
      setLaeuft(false);
    }
  }

  const katIds = new Set(kategorien.filter((k) => k.schritt === schritt.nummer).map((k) => k.id));
  const doks = dokumente.filter((d) => katIds.has(d.kategorieId));
  const vorheriger = schritte.find((s) => s.nummer === schritt.nummer - 1);
  const naechster = schritte.find((s) => s.nummer === schritt.nummer + 1);

  return (
    <div className="wrap schritt-seite">
      <div className="schritt-pfad">
        <button className="btn ghost sm" onClick={onZurueck}>
          ← Übersicht
        </button>
        <span className="s" style={{ margin: 0 }}>
          Schritt {schritt.nummer} von {schritte.length}
        </span>
      </div>

      <header className={`schritt-kopf ${schritt.status}`}>
        <div className={`schritt-nummer gross ${schritt.status}`} aria-hidden="true">
          {erledigt ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L19 7" />
            </svg>
          ) : (
            schritt.nummer
          )}
        </div>
        <div>
          <h2 style={{ margin: 0 }}>{schritt.titel}</h2>
          <p className="schritt-aufgabe">{schritt.beschreibung}</p>
          <p className="s" style={{ margin: '4px 0 0' }}>Üblich dazu: {schritt.ueblich}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {erledigt && (
              <span className="pill ok">
                <span className="dot" />
                Abgeschlossen{schritt.erledigtVon ? ` von ${schritt.erledigtVon}` : ''}
              </span>
            )}
            {offeneRueckfragen > 0 && <span className="badge w">{offeneRueckfragen} offene Rückfrage{offeneRueckfragen === 1 ? '' : 'n'}</span>}
          </div>
        </div>
      </header>

      {children && <div className="schritt-werkzeug">{children}</div>}

      <section className="schritt-unterlagen">
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>
          Unterlagen zu diesem Schritt
          {doks.length > 0 && (
            <span className="badge">
              {doks.filter((d) => d.pruefstatus === 'geprueft').length}/{doks.length}
            </span>
          )}
        </h3>
        <UnterlagenListe kategorien={kategorien} dokumente={dokumente} schritte={schritte} nurSchritt={schritt.nummer} onOeffnen={onDokument} schlicht />
      </section>

      <section className="card schritt-abschluss">
        {erledigt ? (
          <>
            <div>
              <b>Dieser Schritt ist abgeschlossen.</b>
              {schritt.anmerkung && <p className="s" style={{ margin: '4px 0 0' }}>Anmerkung: {schritt.anmerkung}</p>}
            </div>
            <div className="spacer" />
            {!abgeschlossen && (
              <button className="btn" onClick={() => onStatus(schritt.nummer, 'laufend')}>
                Wieder öffnen
              </button>
            )}
            {naechster && (
              <button className="btn primary" onClick={() => onWechsel(naechster.nummer)}>
                Weiter zu Schritt {naechster.nummer} →
              </button>
            )}
          </>
        ) : !abgeschlossen ? (
          <>
            <div style={{ flex: 1 }}>
              <label className="feld" style={{ marginBottom: 0 }}>
                <span>Anmerkung zum Schritt (optional, erscheint im Protokoll)</span>
                <textarea value={anmerkung} onChange={(e) => setAnmerkung(e.target.value)} rows={2} placeholder="z. B. Salden stimmen; Rücklage Aufzug ohne Zuführung geprüft." />
              </label>
            </div>
            <div className="schritt-knoepfe">
              <button className="btn" onClick={() => onRueckfrage(schritt.nummer)}>
                ? Rückfrage zum Schritt
              </button>
              <button className="btn primary" onClick={abschliessen} disabled={laeuft}>
                ✓ Schritt abschließen
              </button>
            </div>
          </>
        ) : (
          <div className="s" style={{ margin: 0 }}>
            Die Prüfung ist abgeschlossen.
          </div>
        )}
      </section>

      <div className="schritt-pfad unten">
        {vorheriger ? (
          <button className="btn ghost sm" onClick={() => onWechsel(vorheriger.nummer)}>
            ← {vorheriger.nummer}. {vorheriger.titel}
          </button>
        ) : (
          <span />
        )}
        {naechster && (
          <button className="btn ghost sm" onClick={() => onWechsel(naechster.nummer)}>
            {naechster.nummer}. {naechster.titel} →
          </button>
        )}
      </div>
    </div>
  );
}
